import Main from "niteroi/packages/sales-distribution/controllers/Main.controller";
import {
    IBusinessPartner, ICodes,
    IInvoice,
    IInvoiceItem,
    ISalesOrder, ISalesOrderItem
} from "niteroi/packages/sales-distribution/models/interfaces";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import JSONModel from "sap/ui/model/json/JSONModel";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import Event from "sap/ui/base/Event";
import Table, {Table$RowSelectionChangeEvent} from "sap/ui/table/Table";
import GenericTile from "sap/m/GenericTile";
import CustomData from "sap/ui/core/CustomData";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import Sorter from "sap/ui/model/Sorter";
import {ListBase$DeleteEvent} from "sap/m/ListBase";
import Input, {Input$SuggestionItemSelectedEvent, Input$ValueHelpRequestEvent} from "sap/m/Input";
import TableSelectDialog, {TableSelectDialog$ConfirmEvent, TableSelectDialog$SearchEvent} from "sap/m/TableSelectDialog";
import ColumnListItem from "sap/m/ColumnListItem";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

export default class Invoices extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);
    private readonly updateInvoiceAnalyticsHandler = this.updateInvoiceAnalytics.bind(this);

    public onInit(): void {
        super.onInit();

        const invoicesModel = this.getModel("salesDistributionInvoiceCollection");
        invoicesModel?.detachRequestCompleted(this.updateInvoiceAnalyticsHandler, this);
        invoicesModel?.attachRequestCompleted(this.updateInvoiceAnalyticsHandler, this);

        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);
            route.detachPatternMatched(this.onPatternMatchedHandler);
            route.attachPatternMatched(this.onPatternMatchedHandler);
        }
        this.updateInvoiceAnalytics();
    }

    public onExit(): void {
        this.getModel("salesDistributionInvoiceCollection")?.detachRequestCompleted(this.updateInvoiceAnalyticsHandler, this);
    }

    public onBeforeRendering(): void {
        super.onBeforeRendering();
        this.initializeDocumentModels();
        this.initializeAnalyticsModel();
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        const routeName = event.getParameter("name");

        this.loadCollectionData("business-partners", "salesDistributionBusinessPartnerCollection");
        this.loadCollectionData("orders", "salesDistributionSalesOrderCollection");

        if (routeName === "sales-distribution/invoices/add") {
            this.getModel("salesDistributionInvoiceDetail").setData(this.getBlankDocumentModel());
            this.getView().setBusy(false);
            return;
        } else {
            const id = (event.getParameter("arguments") as { id?: string })?.id ?? "";
            this.getData(id);
        }
    }

    protected getData(objectId: string = ""): void {
        const componentRoute = "/api/sales-distribution/invoices";
        const url = objectId ? `${componentRoute}/${objectId}` : componentRoute;
        const headers = this.getRequestHeaders();
        const requestModel = this.createRequestModel();
        requestModel.loadData(url, "", true, "GET", false, true, headers).then(() => {
            const modelData = requestModel.getData();
            const modelName = objectId ? "salesDistributionInvoiceDetail" : "salesDistributionInvoiceCollection";
            const model = this.getModel(modelName);
            model.setData(modelData);
            model.refresh(true);
            this.updateInvoiceAnalytics();
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onListSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "idInvoicesTable", ["invoiceNumber", "invoiceId", "status", "billingDate", "postingDate", "serviceDate", "currency", "netValue", "taxValue", "grossValue", "createdAt"]);
    }

    public onPressDashboardTile(): void {
    }

    public onNavButtonPress(): void {
        this.navTo("sales-distribution/invoices", {}, true);
    }

    public onCreateInvoicePress(): void {
        this.setDocumentModel(this.getBlankDocumentModel(), this.getBlankInvoicePartner());
        this.navTo("sales-distribution/invoices/add", {}, true);
    }

    public onRefreshInvoicesPress(): void {
        this.getModel("salesDistributionInvoiceCollection")?.refresh(true);
        this.updateInvoiceAnalytics();
    }

    public onRowSelectionChange(event: Table$RowSelectionChangeEvent): void {
        const rowContext = event.getParameter("rowContext");
        const selectedDocument = rowContext?.getObject() as IInvoice | undefined;

        if (!selectedDocument?.invoiceId) {
            return;
        }

        let partner = this.getBlankInvoicePartner();
        if (selectedDocument.soldToPartnerId) {
            const url = `/api/sales-distribution/business-partners/${selectedDocument.soldToPartnerId}`;
            const method = "GET";
            const businessPartnerModel = this.getModel("salesDistributionBusinessPartnerDetail");
            businessPartnerModel.loadData(url, {}, true, method, false, true, this.getRequestHeaders())
                .then()
                .catch((response: ILoadDataResponse) => {
                    const responseText = JSON.parse(response.responseText);
                    this.showToast(`${responseText.code} - ${responseText.error}`);
            }).finally(() => {
                this.getView().setBusy(false);
            });
            partner = (businessPartnerModel.getData() as IBusinessPartner) ?? partner;
        }

        this.setDocumentModel(selectedDocument, partner);
        this.navTo("sales-distribution/invoices/edit", {id: selectedDocument.invoiceId}, false);
    }

    private initializeDocumentModels(): void {
        if (!this.getModel("salesDistributionInvoiceDetail")) {
            this.setModel(new JSONModel({}), "salesDistributionInvoiceDetail");
        }
        if (!this.getModel("salesDistributionBusinessPartnerDetail")) {
            this.setModel(new JSONModel({}), "salesDistributionBusinessPartnerDetail");
        }
        if (!this.getModel("salesDistributionInvoiceCollection")) {
            this.setModel(new JSONModel([]), "salesDistributionInvoiceCollection");
        }
    }

    private initializeAnalyticsModel(): void {
        const analyticsModel = this.getModel("salesDistributionInvoicesAnalytics");
        if (!analyticsModel) {
            this.setModel(new JSONModel(this.getDefaultAnalytics()), "salesDistributionInvoicesAnalytics");
        }
    }



    protected setDocumentModel(document: IInvoice, partner: IBusinessPartner): void {
        let invoiceModel = this.getModel("salesDistributionInvoiceDetail");
        if (!invoiceModel) {
            invoiceModel = new JSONModel({});
            this.setModel(invoiceModel, "salesDistributionInvoiceDetail");
        }
        invoiceModel.setData({...document});

        const invoicePartnerModel = this.getModel("salesDistributionBusinessPartnerDetail");
        invoicePartnerModel.setData(partner);
    }

    public onPressSaveDraft(): void {
        this.getModel("salesDistributionInvoiceDetail").setProperty("/status", "OPEN");
        this.persist();
    }

    public onPressViewDocument(): void {}

    public onPressSaveDocument(): void {
        const invoiceModel = this.getModel("salesDistributionInvoiceDetail");

        const items = invoiceModel.getProperty("/items") as Array<IInvoiceItem>;
        if (!items || items.length === 0) {
            return;
        }
        const validItems = items.filter((item) => item.description && item.unitPrice && item.quantity && item.materialNumber && item.taxCode && item.unit);
        if (validItems.length === 0) {
            return;
        }
        invoiceModel.setProperty("/items", validItems);

        const soldToPartnerId = invoiceModel.getProperty("/soldToPartnerId");
        if (!soldToPartnerId) {
            return;
        }

        invoiceModel.setProperty("/status", "POSTED");
        const createdAt = new Date().toISOString().replace("T", " ").split(".")[0];
        if (!invoiceModel.getProperty("/billingDate")) {
            invoiceModel.setProperty("/billingDate", createdAt.split(" ")[0]);
        }
        this.persist();
    }

    public persist(): void {
        this.getView().setBusy(true);

        const invoiceModel = this.getModel("salesDistributionInvoiceDetail");
        const invoiceId = invoiceModel.getProperty("/invoiceId") as string;

        if (!invoiceModel.getProperty("/soldToPartnerId")) {
            this.getView().setBusy(false);
            return;
        }

        const componentRoute = "/api/sales-distribution/invoices";
        const url = invoiceId ? `${componentRoute}/${invoiceId}` : componentRoute;
        const method = invoiceId ? "PUT" : "POST";
        invoiceModel.loadData(url, invoiceModel.getJSON(), true, method, false, true, this.getRequestHeaders()).then(() => {
            if (method === "POST") {
                this.navTo("sales-distribution/invoices/edit", {id: invoiceModel.getProperty("/invoiceId")}, false);
            }
            this.getModel("salesDistributionInvoiceCollection")?.refresh(true);
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onPressMarkAsPaid(): void {
        const model = this.getModel("salesDistributionInvoiceDetail");
        model.setProperty("/status", "PAID");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressCreateCreditNote(): void {
        this.navTo("sales-distribution/credits/add", {}, true);
    }

    public onPressCancelButton(): void {
        const model = this.getModel("salesDistributionInvoiceDetail");
        model.setProperty("/status", "CANCELLED");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressAddItem(): void {
        const model = this.getModel("salesDistributionInvoiceDetail");
        const modelData = model.getData() as IInvoice;
        const lastIndex = modelData.items.slice(-1);
        const nextItemNumber = lastIndex[0] ? (lastIndex[0].itemNumber + 10) : 10;
        const newItem: IInvoiceItem = {
            invoiceItemId: "",
            invoiceId: "",
            itemNumber: nextItemNumber,
            materialNumber: "",
            description: "",
            quantity: "0",
            unit: "PC",
            unitPrice: "0",
            taxCode: "A1"
        };
        modelData.items.push(newItem);
        model.refresh(true);
    }

    public onDeleteItem(event: ListBase$DeleteEvent): void {
        const source = event.getParameter("listItem");
        const bindingContext = source.getBindingContext("salesDistributionInvoiceDetail");
        const model = bindingContext.getModel() as JSONModel;
        const invoiceItemId = model.getProperty(bindingContext.getPath()).invoiceItemId;
        const itemIndex = Number(bindingContext.getPath().split("/").pop());
        const modelData = model.getData() as IInvoice;
        const resolvedIndex = invoiceItemId
            ? modelData.items.findIndex((item) => item.invoiceItemId === invoiceItemId)
            : itemIndex;
        if (resolvedIndex > -1) {
            modelData.items.splice(resolvedIndex, 1);
            model.refresh(true);
            this.onChangeLineItem();
        }
    }

    public onChangeLineItem(): void {
        const model = this.getModel("salesDistributionInvoiceDetail");
        const modelData = model.getData() as IInvoice;
        let grossValue = 0.00;
        let netValue = 0.00;
        let taxValue = 0.00;
        modelData.items.forEach((invoiceItem) => {
            if (invoiceItem.unitPrice && invoiceItem.quantity) {
                const codes = this.getModel("salesDistributionCodes").getData() as ICodes;
                if (codes.taxCodes) {
                    const taxItem = codes.taxCodes.find((taxItem) => taxItem.code === invoiceItem.taxCode);

                    const itemNetValue = Number(invoiceItem.unitPrice) * Number(invoiceItem.quantity);
                    const itemTaxValue = itemNetValue * ((taxItem?.amount || 0) / 100);
                    const itemGrossValue = itemNetValue + itemTaxValue;

                    const roundToTwoDecimals = (value: number): number => {
                        return Math.round((value + Number.EPSILON) * 100) / 100;
                    }

                    grossValue += roundToTwoDecimals(itemGrossValue);
                    netValue += roundToTwoDecimals(itemNetValue);
                    taxValue += roundToTwoDecimals(itemTaxValue);
                }
            }
        });
        model.setProperty("/grossValue", grossValue.toString());
        model.setProperty("/netValue", netValue.toString());
        model.setProperty("/taxValue", taxValue.toString());
    }

    public onLineValueChange(): void {
    }

    /**
     * Dynamic analytics dashboard handler that filters and prioritizes the invoice list.
     * Handles the press event on the analytics tiles to dynamically filter the invoice table.
     * @param oEvent The press event object from the GenericTile
     */
    public onAnalyticsTilePress(oEvent: Event): void {
        const oTile = oEvent.getSource() as GenericTile;
        const aCustomData = oTile.getCustomData() as CustomData[];

        const oFilterData = aCustomData.find((oData) => oData.getKey() === "filterType");
        if (!oFilterData) {
            return;
        }

        const sFilterType = oFilterData.getValue() as string;
        const oTable = this.byId("idInvoicesTable") as Table;
        if (!oTable) {
            return;
        }

        const oBinding = oTable.getBinding("rows") as ListBinding;
        if (!oBinding) {
            return;
        }

        const aFilters: Filter[] = [];
        const aSorters: Sorter[] = [];

        switch (sFilterType) {
            case "OPEN":
                aFilters.push(new Filter("status", FilterOperator.EQ, "OPEN"));
                break;
            case "HIGH_VALUE_PENDING":
                aFilters.push(new Filter("status", FilterOperator.NE, "PAID"));
                aFilters.push(new Filter("status", FilterOperator.NE, "CANCELLED"));
                aSorters.push(new Sorter("grossValue", true, false, (a: string, b: string) => Number(a) - Number(b)));
                break;
            case "PAID":
                aFilters.push(new Filter("status", FilterOperator.EQ, "PAID"));
                break;
            case "PENDING":
                aFilters.push(new Filter([
                    new Filter("status", FilterOperator.EQ, "POSTED"),
                ], false));
                break;
            case "TOTAL":
            default:
                aSorters.push(new Sorter("billingDate", true));
                break;
        }

        oBinding.filter(aFilters);
        oBinding.sort(aSorters);
    }

    private updateInvoiceAnalytics(): void {
        const invoicesModel = this.getModel("salesDistributionInvoiceCollection");
        const analyticsModel = this.getModel("salesDistributionInvoicesAnalytics");

        if (!analyticsModel) {
            return;
        }

        const invoices = (invoicesModel?.getData() as IInvoice[]) ?? [];

        if (!Array.isArray(invoices) || invoices.length === 0) {
            analyticsModel.setData(this.getDefaultAnalytics());
            return;
        }

        // --- Existing Operational Calculations ---
        const paidInvoices = invoices.filter((invoice) => invoice.status === "PAID").length;
        const pendingInvoices = invoices.filter((invoice) => invoice.status === "POSTED" || invoice.status === "OPEN").length;
        const totalItems = invoices.reduce((sum, invoice) => sum + (invoice.items?.length ?? 0), 0);
        const paidRatio = Math.round((paidInvoices / invoices.length) * 100);

        // --- New Enhanced KPI Calculations for Refactored Dashboard Tiles ---

        // 1. Workflow Bottleneck: Invoices Stuck in Draft
        const draftInvoicesCount = invoices.filter((invoice) => invoice.status === "OPEN").length;

        // 2. Risk Management: Outstanding Cash Above a Defined Limit
        const HIGH_VALUE_THRESHOLD = 50000; // Define high-value threshold limit (e.g., €50,000)
        const highValueOutstandingCount = invoices.filter((invoice) =>
            invoice.status !== "PAID" &&
            invoice.status !== "CANCELLED" &&
            Number(invoice.grossValue || 0) >= HIGH_VALUE_THRESHOLD
        ).length;

        // 3. Revenue Leakage Control: Volume of Cancelled Billings
        const totalCancelledValue = invoices
            .filter((invoice) => invoice.status === "CANCELLED")
            .reduce((sum, invoice) => sum + Number(invoice.grossValue || 0), 0);

        const cancelledThresholdLimit = 25000; // Tolerance target limit baseline for microchart comparison

        // 4. Logistics Alignment: Counting Missing Document Links
        const unlinkedDeliveriesCount = invoices.filter((invoice) => !invoice.deliveryNoteId).length;

        // 5. Liquidity Realization & Velocity (Aggregated Gross Financial Metrics)
        const totalGrossValue = invoices.reduce((sum, invoice) => sum + Number(invoice.grossValue || 0), 0);

        const paidGrossValue = invoices
            .filter((invoice) => invoice.status === "PAID")
            .reduce((sum, invoice) => sum + Number(invoice.grossValue || 0), 0);

        const targetGrossValue = 1500000; // Dynamic or seasonal periodic sales billing milestone target

        // --- Update Analytics JSON Structure ---
        analyticsModel.setData({
            // Existing parameters
            totalInvoices: invoices.length,
            paidInvoices,
            pendingInvoices,
            totalItems,
            paidRatio,
            draftInvoicesCount,
            highValueOutstandingCount,
            totalCancelledValue: Math.round(totalCancelledValue),
            cancelledThresholdLimit,
            unlinkedDeliveriesCount,
            totalGrossValue: Math.round(totalGrossValue),
            paidGrossValue: Math.round(paidGrossValue),
            targetGrossValue
        });
    }

    private getDefaultAnalytics(): object {
        return {
            totalInvoices: 0,
            paidInvoices: 0,
            pendingInvoices: 0,
            missingBuyerEmailInvoices: 0,
            totalItems: 0,
            paidRatio: 0,
            draftInvoicesCount: 0,
            highValueOutstandingCount: 0,
            totalCancelledValue: 0,
            cancelledThresholdLimit: 25000,
            unlinkedDeliveriesCount: 0,
            totalGrossValue: 0,
            paidGrossValue: 0,
            targetGrossValue: 1500000
        };
    }

    protected getBlankDocumentModel(): IInvoice {
        return {
            invoiceId: "",
            invoiceNumber: "",
            status: "OPEN",
            billingDate: "",
            postingDate: null,
            serviceDate: null,
            currency: "EUR",
            paymentTerms: "",
            netValue: "0.00",
            taxValue: "0.00",
            grossValue: "0.00",
            createdAt: "",
            updatedAt: "",
            soldToPartnerId: null,
            salesOrderId: null,
            deliveryNoteId: null,
            items: []
        };
    }

    protected getBlankInvoicePartner(): IBusinessPartner {
        return {
            businessPartnerId: "",
            customerNumber: "",
            legalName1: "",
            legalName2: "",
            vatId: "",
            taxNumber: "",
            partnerRole: "",
            isActive: false,
            createdAt: "",
            updatedAt: "",
            companyId: "",
            addresses: [],
            salesAreas: []
        };
    }

    /**
     * Internal helper to update the invoice model state
     */
    protected _updatePartner(partner: IBusinessPartner): void {
        const invoiceModel = this.getView()?.getModel("salesDistributionInvoiceDetail") as JSONModel;
        if (invoiceModel) {
            invoiceModel.setProperty("/soldToPartnerId", partner.businessPartnerId);
            const invoicePartnerModel = this.getModel("salesDistributionBusinessPartnerDetail");
            invoicePartnerModel.setData(partner);
        }
    }

    public onPressBusinessPartnerLegalName(): void {
        this.getModel("salesDistributionBusinessPartnerDetail").setData(this.getBlankInvoicePartner());
        const invoiceModel = this.getModel("salesDistributionInvoiceDetail");
        invoiceModel.setProperty("/soldToPartnerId", "");
        (this.byId("businessPartnerInput") as Input).fireValueHelpRequest();
    }

    /**
     * Handles selection made directly from the type-ahead suggestion dropdown.
     */
    public onSuggestionItemSelectedBusinessPartner(event: Input$SuggestionItemSelectedEvent): void {
        const selectedItem = event.getParameter("selectedRow") as ColumnListItem;
        const rowBindingContext = selectedItem.getBindingContext("salesDistributionBusinessPartnerCollection");
        if (!rowBindingContext) {
            return;
        }

        const partner = rowBindingContext.getObject() as IBusinessPartner;
        this._updatePartner(partner);
    }

    /**
     * Opens the Sales Order Item Value Help Dialog
     */
    public onPressAddFromSalesOrder(event: Input$ValueHelpRequestEvent): void {
        const dialog = this.byId("salesOrderItemValueHelpDialog") as TableSelectDialog;
        if (dialog) {
            dialog.open(event.getSource().getValue());
        }
    }

    /**
     * Handles selection from sales order suggestion dropdown
     */
    public onSuggestionItemSelectedSalesOrder(event: Input$SuggestionItemSelectedEvent): void {
        const selectedItem = event.getParameter("selectedRow") as ColumnListItem;
        const rowBindingContext = selectedItem.getBindingContext("salesDistributionSalesOrderCollection");
        if (!rowBindingContext) {
            return;
        }

        const salesOrder = rowBindingContext.getObject() as ISalesOrder;
        this._updateInvoiceSalesOrder(salesOrder);
    }

    /**
     * Handles search in the Sales Order Item Value Help Dialog
     */
    public onSalesOrderItemValueHelpSearch(event: TableSelectDialog$SearchEvent): void {
        const value = event.getParameter("value") || "";
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;

        // Filter for IN_PROCESS sales orders only (ready for invoicing)
        const inProcessFilter = new Filter("status", FilterOperator.EQ, "IN_PROCESS");

        if (!value) {
            binding.filter([inProcessFilter]);
            return;
        }

        const searchFilters = [
            new Filter("salesOrderNumber", FilterOperator.Contains, value),
            new Filter("soldToPartnerId", FilterOperator.Contains, value),
            new Filter("description", FilterOperator.Contains, value),
            new Filter("items/0/description", FilterOperator.Contains, value),
            new Filter("items/0/materialNumber", FilterOperator.Contains, value)
        ];

        const combinedSearchFilter = new Filter({filters: searchFilters, and: false});
        const finalFilter = new Filter({filters: [inProcessFilter, combinedSearchFilter], and: true});

        binding.filter([finalFilter]);
    }

    /**
     * Confirms selection from the Sales Order Item Value Help Dialog
     */
    public onSalesOrderItemValueHelpConfirm(event: TableSelectDialog$ConfirmEvent): void {
        const selectedItems = event.getParameter("selectedItems");
        if (!selectedItems || selectedItems.length === 0) {
            this._resetSalesOrderItemValueHelpDialogFilters(event);
            return;
        }

        // Get the selected sales order
        const context = selectedItems[0].getBindingContext("salesDistributionSalesOrderCollection");
        if (!context) {
            this._resetSalesOrderItemValueHelpDialogFilters(event);
            return;
        }

        const salesOrder = context.getObject() as ISalesOrder;
        this._updateInvoiceSalesOrder(salesOrder);

        this._resetSalesOrderItemValueHelpDialogFilters(event);
    }

    /**
     * Cleans up filters if the user cancels the sales order item dialog
     */
    public onSalesOrderItemValueHelpCancel(event: Event): void {
        this._resetSalesOrderItemValueHelpDialogFilters(event);
    }

    /**
     * Internal helper to update invoice with sales order data
     */
    private _updateInvoiceSalesOrder(salesOrder: ISalesOrder): void {
        const invoiceModel = this.getView()?.getModel("salesDistributionInvoiceDetail") as JSONModel;
        if (invoiceModel) {
            invoiceModel.setProperty("/soldToPartnerId", salesOrder.soldToPartnerId);
            const businessPartnerCollection = this.getModel("salesDistributionBusinessPartnerCollection");
            const businessPartner = (businessPartnerCollection.getData() as Array<IBusinessPartner>).find((partner) => salesOrder.soldToPartnerId === partner.businessPartnerId);
            if (businessPartner) {
                this.getModel("salesDistributionBusinessPartnerDetail").setData(businessPartner);
            }

            invoiceModel.setProperty("/salesOrderId", salesOrder.salesOrderId);
            this.getModel("salesDistributionSalesOrderDetail").setData(salesOrder);

            if (salesOrder.items && salesOrder.items.length > 0) {
                const invoiceItems: Array<IInvoiceItem> = salesOrder.items.map((item: ISalesOrderItem) => {
                    return {
                        invoiceItemId: "",
                        invoiceId: invoiceModel.getProperty("/invoiceId") ?? "",
                        itemNumber: item.itemNumber,
                        materialNumber: item.materialNumber,
                        description: item.description,
                        quantity: item.quantity,
                        unit: item.unit,
                        unitPrice: item.unitPrice,
                        taxCode: item.taxCode
                    };
                });
                invoiceModel.setProperty("/items", invoiceItems);
                this.onChangeLineItem();
            }
        }
    }

    /**
     * Handles press on sales order number object status
     */
    public onPressSalesOrderNumber(): void {
        this.getModel("salesDistributionSalesOrderDetail").setData({});
        const invoiceModel = this.getModel("salesDistributionInvoiceDetail");
        invoiceModel.setProperty("/salesOrderId", "");
        invoiceModel.setProperty("/soldToPartnerId", "");
        (this.byId("salesOrderIdInput") as Input).fireValueHelpRequest();
    }

    /**
     * Internal helper to reset sales order item dialog filters
     */
    private _resetSalesOrderItemValueHelpDialogFilters(event: Event): void {
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;
        const inProcessFilter = new Filter("status", FilterOperator.EQ, "IN_PROCESS");
        binding?.filter([inProcessFilter]);
    }
}
